import { createStore } from 'zustand/vanilla'
import { useRiderStore } from './rider'

// MapLibre pitch increases toward the horizon. A high default produces a low,
// track-level chase camera instead of the previous elevated map view.
export const WATCH_DEFAULT_PITCH = 78
export const WATCH_DEFAULT_ZOOM = 18.2

interface VehicleWatchState {
  watchedVehicleId: string | null
  orbitYaw: number
  pitch: number
  zoom: number
  watchVehicle: (vehicleId: string) => void
  stopWatching: () => void
  orbitBy: (yawDelta: number, pitchDelta: number) => void
  zoomBy: (delta: number) => void
  resetCamera: () => void
}

const clamp = (value: number, min: number, max: number) =>
  Math.max(min, Math.min(max, value))

export const useVehicleWatchStore = createStore<VehicleWatchState>((set) => ({
  watchedVehicleId: null,
  orbitYaw: 0,
  pitch: WATCH_DEFAULT_PITCH,
  zoom: WATCH_DEFAULT_ZOOM,

  watchVehicle: (vehicleId) => set({
    watchedVehicleId: vehicleId,
    orbitYaw: 0,
    pitch: WATCH_DEFAULT_PITCH,
    zoom: WATCH_DEFAULT_ZOOM,
  }),
  stopWatching: () => set({ watchedVehicleId: null }),
  orbitBy: (yawDelta, pitchDelta) => set((state) => ({
    orbitYaw: ((state.orbitYaw + yawDelta + 540) % 360) - 180,
    pitch: clamp(state.pitch + pitchDelta, 45, 84),
  })),
  zoomBy: (delta) => set((state) => ({ zoom: clamp(state.zoom + delta, 16.5, 20) })),
  resetCamera: () => set({ orbitYaw: 0, pitch: WATCH_DEFAULT_PITCH, zoom: WATCH_DEFAULT_ZOOM }),
}))

// Boarding ends watching: the two are different cameras on the same train, and the rider
// panel's own view wins once you're on board. Enforced here rather than at each board
// button, because there is already more than one way to board (the vehicle sheet and the
// rider panel's "board this" prompt) and only one of them used to remember to do it.
// Keyed on the tracked vehicle rather than the phase, so switching rides while already
// aboard clears the watch too. If the rider enabled the onboard 3D toggle, stepping off
// also exits that vehicle's chase camera instead of leaving watch mode orphaned.
useRiderStore.subscribe((state, prev) => {
  if (
    state.trackedVehicleId !== prev.trackedVehicleId &&
    (state.trackedVehicleId ||
      useVehicleWatchStore.getState().watchedVehicleId === prev.trackedVehicleId)
  ) {
    useVehicleWatchStore.getState().stopWatching()
  }
})
